import {
  List,
  useTable,
  ShowButton,
  DeleteButton,
  DateField,
  useSelect,
} from "@refinedev/antd";
import { useList, useUpdate, type CrudFilters } from "@refinedev/core";
import {
  Table,
  Space,
  Tag,
  Typography,
  Alert,
  Row,
  Col,
  Card,
  Select,
  Input,
  Button,
  Tooltip,
  Grid,
} from "antd";
import {
  EyeInvisibleOutlined,
  FlagOutlined,
  LockOutlined,
  SearchOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import { useMemo, useState } from "react";
import type { Entry, Brain, Category, Modo } from "@/lib/types";
import { CategoryTag } from "@/components/CategoryTag";
import { StatCard } from "@/components/StatCard";
import { truncate } from "@/lib/format";
import { CATEGORY_LABELS } from "@/lib/constants";

const { Text } = Typography;
const { useBreakpoint } = Grid;

const CATEGORY_OPTIONS = (Object.keys(CATEGORY_LABELS) as Category[]).map((c) => ({
  label: CATEGORY_LABELS[c],
  value: c,
}));

const MODO_OPTIONS: { label: string; value: Modo }[] = [
  { label: "Normal", value: "normal" },
  { label: "Reunião", value: "reuniao" },
  { label: "Desabafo", value: "desabafo" },
];

const MODO_COLOR: Record<Modo, string> = {
  normal: "default",
  reuniao: "blue",
  desabafo: "magenta",
};

const MODO_LABEL: Record<Modo, string> = {
  normal: "Normal",
  reuniao: "Reunião",
  desabafo: "Desabafo",
};

/**
 * /admin/entries — moderação de entries (ver / ocultar / excluir).
 *
 * Como obtém os dados (MOCK MODE): `useTable` lista paginado contra os fixtures
 * (filtros server-like: categoria/brain/modo/flagged/privacidade + busca por
 * texto). Um `useList({ pagination:{ mode:"off" } })` traz tudo para os KPIs e
 * para o seletor de Brain. A mesma query atende GET /admin/entries quando a API
 * real entrar (VITE_MOCK=false).
 *
 * PRIVACIDADE (LGPD §11): conteúdo de entradas `is_private` (modo Desabafo /
 * saúde sensível) NUNCA é exibido na lista — só o rótulo "privado (oculto)".
 * "Ocultar" é uma ação de moderação que marca a entrada como privada (redige o
 * conteúdo) via useUpdate, sem apagá-la; "Excluir" remove de fato.
 */
export const EntryList: React.FC = () => {
  const screens = useBreakpoint();

  // Filtros controlados localmente → repassados ao useTable.
  const [search, setSearch] = useState("");
  const [categoria, setCategoria] = useState<Category | undefined>();
  const [modo, setModo] = useState<Modo | undefined>();
  const [brainId, setBrainId] = useState<string | undefined>();
  const [flag, setFlag] = useState<"flagged" | "private" | undefined>();

  const filters = useMemo<CrudFilters>(() => {
    const f: CrudFilters = [];
    if (search.trim())
      f.push({ field: "texto_original", operator: "contains", value: search.trim() });
    if (categoria) f.push({ field: "categoria", operator: "eq", value: categoria });
    if (modo) f.push({ field: "modo", operator: "eq", value: modo });
    if (brainId) f.push({ field: "brain_id", operator: "eq", value: brainId });
    if (flag === "flagged") f.push({ field: "flagged", operator: "eq", value: true });
    if (flag === "private") f.push({ field: "is_private", operator: "eq", value: true });
    return f;
  }, [search, categoria, modo, brainId, flag]);

  const { tableProps } = useTable<Entry>({
    syncWithLocation: true,
    filters: { permanent: filters },
    sorters: { initial: [{ field: "created_at", order: "desc" }] },
  });

  // Todas as entradas (sem paginar) para os KPIs de moderação.
  const { data: allData, isLoading: loadingAll } = useList<Entry>({
    resource: "entries",
    pagination: { mode: "off" },
  });
  const all = useMemo(() => allData?.data ?? [], [allData]);
  const kpis = useMemo(() => {
    let flagged = 0;
    let priv = 0;
    let sensitive = 0;
    for (const e of all) {
      if (e.flagged) flagged++;
      if (e.is_private) priv++;
      if (e.categoria === "saude" || e.modo === "desabafo") sensitive++;
    }
    return { total: all.length, flagged, priv, sensitive };
  }, [all]);

  // Seletor de Brain (rótulo legível) a partir do resource brains.
  const { selectProps: brainSelectProps } = useSelect<Brain>({
    resource: "brains",
    optionLabel: (b) => `${b.emoji ?? "🧠"} ${b.nome}`,
    optionValue: "id",
    pagination: { mode: "off" },
  });

  // "Ocultar" = marcar como privado (redige o conteúdo) — moderação reversível.
  const { mutate: update, isLoading: hiding } = useUpdate();

  const hasFilters = !!(search || categoria || modo || brainId || flag);
  const clearFilters = () => {
    setSearch("");
    setCategoria(undefined);
    setModo(undefined);
    setBrainId(undefined);
    setFlag(undefined);
  };

  return (
    <List title="Moderação · Entries">
      <Alert
        type="warning"
        showIcon
        icon={<LockOutlined />}
        style={{ marginBottom: 16 }}
        message="Conteúdo sensível"
        description="Entradas do modo Desabafo e dados de saúde são privados (LGPD §11): o texto fica oculto na lista. Use “Ocultar” para redigir uma entrada denunciada sem apagá-la."
      />

      {/* KPIs de moderação — agregados de todas as entradas. */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} lg={6}>
          <StatCard title="Entradas" value={kpis.total} loading={loadingAll} hint="total no período" />
        </Col>
        <Col xs={12} lg={6}>
          <StatCard
            title="Denunciadas"
            value={kpis.flagged}
            loading={loadingAll}
            prefix={<FlagOutlined />}
            hint="aguardando revisão"
          />
        </Col>
        <Col xs={12} lg={6}>
          <StatCard
            title="Privadas"
            value={kpis.priv}
            loading={loadingAll}
            prefix={<EyeInvisibleOutlined />}
            hint="conteúdo redigido"
          />
        </Col>
        <Col xs={12} lg={6}>
          <StatCard title="Sensíveis" value={kpis.sensitive} loading={loadingAll} hint="saúde / desabafo" />
        </Col>
      </Row>

      {/* Barra de filtros. */}
      <Card className="mel-glass" bordered={false} style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={8} lg={7}>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Buscar no texto…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Col>
          <Col xs={12} md={4} lg={4}>
            <Select
              allowClear
              style={{ width: "100%" }}
              placeholder="Categoria"
              value={categoria}
              onChange={setCategoria}
              options={CATEGORY_OPTIONS}
            />
          </Col>
          <Col xs={12} md={4} lg={4}>
            <Select
              allowClear
              style={{ width: "100%" }}
              placeholder="Modo"
              value={modo}
              onChange={setModo}
              options={MODO_OPTIONS}
            />
          </Col>
          <Col xs={24} md={8} lg={5}>
            <Select
              allowClear
              showSearch
              loading={brainSelectProps.loading}
              optionFilterProp="label"
              style={{ width: "100%" }}
              placeholder="Brain"
              value={brainId}
              onChange={setBrainId}
              options={(brainSelectProps.options ?? []).map((o) => ({
                label: String(o.label),
                value: String(o.value),
              }))}
            />
          </Col>
          <Col xs={16} md={4} lg={3}>
            <Select
              allowClear
              style={{ width: "100%" }}
              placeholder="Estado"
              value={flag}
              onChange={setFlag}
              options={[
                { label: "Denunciadas", value: "flagged" },
                { label: "Privadas", value: "private" },
              ]}
            />
          </Col>
          <Col xs={8} md={24} lg={1}>
            <Tooltip title="Limpar filtros">
              <Button
                icon={<ClearOutlined />}
                onClick={clearFilters}
                disabled={!hasFilters}
                block={!screens.lg}
              />
            </Tooltip>
          </Col>
        </Row>
      </Card>

      <Card className="mel-glass" bordered={false}>
        <Table
          {...tableProps}
          rowKey="id"
          scroll={{ x: 1100 }}
          onRow={(row: Entry) => ({
            style: row.flagged ? { background: "rgba(239,68,68,0.05)" } : undefined,
          })}
        >
          <Table.Column
            dataIndex="categoria"
            title="Categoria"
            render={(c: Entry["categoria"]) => <CategoryTag value={c} />}
          />
          <Table.Column<Entry>
            title="Texto"
            render={(_, row) =>
              row.is_private ? (
                <Space size={6}>
                  <LockOutlined style={{ color: "#C2185B" }} />
                  <Tag color="magenta">privado (oculto)</Tag>
                </Space>
              ) : (
                <Text>{truncate(row.texto_corrigido ?? row.texto_original, 90)}</Text>
              )
            }
          />
          <Table.Column
            dataIndex="modo"
            title="Modo"
            render={(m: Entry["modo"]) => (
              <Tag color={MODO_COLOR[m] ?? "default"}>{MODO_LABEL[m] ?? m}</Tag>
            )}
          />
          <Table.Column<Entry>
            title="Estado"
            render={(_, row) => (
              <Space size={4}>
                {row.flagged && (
                  <Tag color="red" icon={<FlagOutlined />}>
                    denunciado
                  </Tag>
                )}
                {row.is_private && (
                  <Tag color="magenta" icon={<EyeInvisibleOutlined />}>
                    privado
                  </Tag>
                )}
                {!row.flagged && !row.is_private && <Tag>ok</Tag>}
              </Space>
            )}
          />
          <Table.Column
            dataIndex="created_at"
            title="Criado"
            render={(v: string) => <DateField value={v} format="DD/MM/YYYY HH:mm" />}
          />
          <Table.Column<Entry>
            title="Ações"
            fixed="right"
            width={150}
            render={(_, row) => (
              <Space>
                <ShowButton hideText size="small" recordItemId={row.id} />
                {/* Ocultar = redigir conteúdo (marca como privado) sem apagar. */}
                <Tooltip
                  title={row.is_private ? "Já está oculto" : "Ocultar conteúdo (moderação)"}
                >
                  <Button
                    size="small"
                    icon={<EyeInvisibleOutlined />}
                    disabled={row.is_private}
                    loading={hiding}
                    onClick={() =>
                      update({
                        resource: "entries",
                        id: row.id,
                        values: { is_private: true },
                        successNotification: () => ({
                          type: "success",
                          message: "Entrada ocultada",
                          description: "O conteúdo foi redigido por moderação.",
                        }),
                      })
                    }
                  />
                </Tooltip>
                {/* DELETE /admin/entries/:id (moderação) */}
                <DeleteButton
                  hideText
                  size="small"
                  recordItemId={row.id}
                  confirmTitle="Remover esta entrada por moderação?"
                />
              </Space>
            )}
          />
        </Table>
      </Card>
    </List>
  );
};
